import {
	createContext,
	createElement,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { i18next, persistLanguage } from "../i18n/config";
import { isSupportedLanguage } from "../i18n/types";

interface AuthContextValue {
	token: string | null;
	name: string | null;
	onboardingComplete: boolean | null;
	login: (
		token: string,
		name: string | null,
		language?: string | null,
		onboardingComplete?: boolean | null,
	) => void;
	logout: () => void;
	updateName: (name: string) => void;
	setOnboardingComplete: (v: boolean) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
	const [token, setToken] = useState<string | null>(() =>
		localStorage.getItem("token"),
	);
	const [name, setName] = useState<string | null>(() =>
		localStorage.getItem("userName"),
	);
	const [onboardingComplete, setOnboardingCompleteState] = useState<
		boolean | null
	>(() => {
		const stored = localStorage.getItem("onboardingComplete");
		return stored === null ? null : stored === "true";
	});

	const setOnboardingComplete = useCallback((v: boolean) => {
		localStorage.setItem("onboardingComplete", String(v));
		setOnboardingCompleteState(v);
	}, []);

	const login = useCallback(
		(
			t: string,
			n: string | null,
			language?: string | null,
			oc?: boolean | null,
		) => {
			localStorage.setItem("token", t);
			if (n) localStorage.setItem("userName", n);
			if (oc !== undefined && oc !== null) {
				localStorage.setItem("onboardingComplete", String(oc));
				setOnboardingCompleteState(oc);
			}
			setToken(t);
			setName(n);
			if (language && isSupportedLanguage(language)) {
				i18next.changeLanguage(language);
				persistLanguage(language);
			}
		},
		[],
	);

	const logout = useCallback(() => {
		localStorage.removeItem("token");
		localStorage.removeItem("userName");
		localStorage.removeItem("onboardingComplete");
		setToken(null);
		setName(null);
		setOnboardingCompleteState(null);
	}, []);

	const updateName = useCallback((n: string) => {
		localStorage.setItem("userName", n);
		setName(n);
	}, []);

	useEffect(() => {
		const handler = () => {
			localStorage.removeItem("token");
			localStorage.removeItem("userName");
			localStorage.removeItem("onboardingComplete");
			setToken(null);
			setName(null);
			setOnboardingCompleteState(null);
		};
		window.addEventListener("auth:expired", handler);
		return () => window.removeEventListener("auth:expired", handler);
	}, []);

	return createElement(
		AuthContext.Provider,
		{
			value: {
				token,
				name,
				onboardingComplete,
				login,
				logout,
				updateName,
				setOnboardingComplete,
			},
		},
		children,
	);
}

export function useAuth(): AuthContextValue {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
	return ctx;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
	const { token, onboardingComplete } = useAuth();
	const location = useLocation();

	if (!token) return <Navigate to="/onboarding" replace />;

	if (onboardingComplete === false && location.pathname === "/home") {
		return <Navigate to="/onboarding/prompts" replace />;
	}

	return <>{children}</>;
}
